import React from 'react'
import { connect } from 'react-redux'
import { AppState } from '@chegg-wt/core/state'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { hasSideViewForRoute } from '@chegg-wt/core/utils/adframe'
import { AppRouteName, Source, Contributor } from '@chegg-wt/core/types/core'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { currentRouteSelector } from '@chegg-wt/core/state/app/selectors'
import { citationInProgressSelector } from '@chegg-wt/core/state/citation/selectors'
import * as Styled from './styled'

export const TESTID_CITE_BUTTON = 'cite-button'
export const TESTID_LIBRARY_LINK = 'find-my-library-link'
export const TESTID_WEBSITE_LINK = 'website-link'

const contributorsToString = (cons: Contributor[], style: string): string => {
  const chapterAuthor = cons.find((c) => c.function === 'section_author')
  const contribs = chapterAuthor && style === 'apa' ? [chapterAuthor] : cons
  const separator = style.startsWith('mla') ? 'and ' : '& '
  const lastNames = contribs.filter((c) => c.last !== '').map((c) => c.last)
  const count = contribs.length
  const ampersandLast = (x, i, xs) => (i + 1 === xs.length ? `& ${x}` : x)

  if (count === 0) return ''
  if (count === 1 || count === 2) return lastNames.join(` ${separator}`)
  if (style === 'apa' && count <= 5) return lastNames.map(ampersandLast).join(', ')
  return `${lastNames[0]} et al.`
}

const descriptionToString = (result: Source, style: string): string => {
  const { display, data } = result
  const { publisher } = display
  const year = display.year || display.date
  const contribs = contributorsToString(data.contributors, style)

  return [contribs, publisher, year].filter((x) => x).join(' - ')
}

interface StateProps {
  currentRoute: AppRouteName
  citationCreateInProgress: number[]
}

interface DispatchProps {
  findAtMyLibraryClick: () => void
}

interface Props {
  result: Source
  currentStyle: string
  cite: () => void
  index: number
}

export const ResultCard: React.FC<Props & StateProps & DispatchProps> = ({
  result,
  currentStyle,
  cite,
  currentRoute,
  index,
  findAtMyLibraryClick,
  citationCreateInProgress,
}) => {
  const { data, display } = result
  const { source, website, autocite } = data
  const { url } = autocite || {}

  const title =
    source === 'website' && website.title
      ? website.title
      : display.title || display.page_title

  const description =
    website && website.summary
      ? website.summary
      : descriptionToString(result, currentStyle)

  const href = autocite && url ? url : display.url
  const linkText = autocite && url ? url : display.displayurl
  const isWebsite = source === 'website' || source === 'webpage'
  const hasLibraryLink = !isWebsite && result.link
  const hasSideView = hasSideViewForRoute(currentRoute)
  const isLoading: boolean = citationCreateInProgress.includes(index)

  return (
    <Styled.Result data-test="citation-card" hasSideView={hasSideView}>
      <Styled.ResultInfo data-test="card-info">
        <Styled.ResultTitle id={`--title-${index}`}>{title}</Styled.ResultTitle>
        <Styled.ResultDescription id={`--description-${index}`}>
          {description}
        </Styled.ResultDescription>
        {isWebsite && (
          <Styled.ResultLink href={href} target="_blank" data-test={TESTID_WEBSITE_LINK}>
            {linkText}
          </Styled.ResultLink>
        )}
        {hasLibraryLink && (
          <Styled.ResultLink
            href={result.link}
            target="_blank"
            aria-labelledby={`--description-${index}`}
            onClick={findAtMyLibraryClick}
            data-test={TESTID_LIBRARY_LINK}
          >
            Find at my library
          </Styled.ResultLink>
        )}
      </Styled.ResultInfo>
      <Styled.ResultButton
        onClick={cite}
        data-test={TESTID_CITE_BUTTON}
        size="small"
        hasSideView={hasSideView}
        aria-describedby={`--title-${index} --description-${index}`}
        isLoading={isLoading}
      >
        <Styled.ResultButtonText>Cite</Styled.ResultButtonText>
      </Styled.ResultButton>
    </Styled.Result>
  )
}

const mapStateToProps = (state: AppState): StateProps => ({
  currentRoute: currentRouteSelector(state),
  citationCreateInProgress: citationInProgressSelector(state),
})

const mapDispatchToProps = {
  findAtMyLibraryClick: () => userClick({ origin: origins.findAtMyLibraryClick }),
}

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(ResultCard)
